import { Box, createStyles, ActionIcon, useMantineTheme } from '@mantine/core';
import Link from 'next/link';
import React from 'react';

import Container from '~/domains/design-system/Container';
import { Text } from '~/domains/design-system/Text';
import { IconChevronRight } from '~/domains/design-system/icons/IconChevronRight';
import { IconChevronLeft } from '~/domains/design-system/icons/IconChevronLeft';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import dt from '~/testing';

const useStyles = createStyles((theme) => ({
  wrapper: {
    background: theme.colors.gray2[7],
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      background: theme.colors.gray2[6],
    },
  },
  container: {
    flex: 1,
    height: '56px',
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
      height: '48px',
    },
    paddingTop: /* eslint-disable-line local-rules/use-theme-tokens */ '18px',
    paddingBottom: /* eslint-disable-line local-rules/use-theme-tokens */ '18px',
    display: 'flex',
    gap: /* eslint-disable-line local-rules/use-theme-tokens */ '12px',
    alignItems: 'center',
  },
}));

function BreadcrumbsWrapper({ children }: { children: React.ReactNode }) {
  const { classes } = useStyles();

  return (
    <Box component="nav" className={classes.wrapper}>
      <Container className={classes.container}>{children}</Container>
    </Box>
  );
}

function BreadcrumbsItem({
  isMobile,
  children,
  href,
  highlightItem,
}: {
  isMobile: boolean;
  children: React.ReactNode;
  href?: string;
  highlightItem?: boolean;
}) {
  if (href) {
    return (
      <Text
        component={Link}
        href={href}
        variant="title-medium"
        sx={(theme) => ({
          color: highlightItem ? theme.colors.blue[4] : theme.white,
          textDecoration: 'none',
          '&:hover': {
            color: theme.colors.blue[4],
          },
        })}
      >
        {children}
      </Text>
    );
  }

  return (
    <Text
      variant={isMobile ? 'headline-medium' : 'title-medium'}
      color={isMobile ? 'white' : 'blue.4'}
      sx={{
        ...(isMobile && {
          textAlign: 'center',
          flex: 1,
          width: '100%',
          overflow: 'hidden',
        }),
      }}
    >
      {children}
    </Text>
  );
}

function BreadcrumbsBackLink({
  href,
  visibility = 'visible',
}: {
  href: string;
  visibility?: 'visible' | 'hidden';
}) {
  const theme = useMantineTheme();

  return (
    <ActionIcon
      data-test-id={dt.channel.invitesManager.IconChevronLeft}
      component={Link}
      href={href}
      // eslint-disable-next-line @typescript-eslint/no-shadow
      sx={(theme) => ({
        visibility,
        '&:hover': {
          background: theme.colors.gray2[4],
        },
      })}
    >
      <IconChevronLeft color={theme.white} />
    </ActionIcon>
  );
}

type LinkWithRequiredHref = { href: string; label: React.ReactNode };
type LinkWithoutRequiredHref = { href?: string; label: React.ReactNode };

type BreadcrumbsProps = {
  links: [...LinkWithRequiredHref[], LinkWithoutRequiredHref];
  children?: React.ReactNode;
  forceMobile?: boolean;
  isBackButtonVisible?: boolean;
  highlightLastItem?: boolean;
  leftContent?: React.ReactNode;
};

export function Breadcrumbs({
  links,
  children,
  forceMobile,
  isBackButtonVisible = true,
  highlightLastItem = false,
  leftContent,
}: BreadcrumbsProps) {
  const isMobile = useIsMobile() || forceMobile;
  const theme = useMantineTheme();

  const { classes } = useStyles();

  return (
    <BreadcrumbsWrapper>
      {isMobile ? (
        <>
          {leftContent && !isBackButtonVisible && <div>{leftContent}</div>}
          {(isBackButtonVisible || children) && (
            <BreadcrumbsBackLink
              href={links[links.length - 2].href}
              visibility={isBackButtonVisible ? 'visible' : 'hidden'}
            />
          )}
          <BreadcrumbsItem isMobile={isMobile}>{links[links.length - 1].label}</BreadcrumbsItem>
          {children ||
            (isBackButtonVisible && <BreadcrumbsBackLink href="#" visibility="hidden" />)}
        </>
      ) : (
        <>
          <Box className={classes.container}>
            {links.map((link, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>
                <BreadcrumbsItem
                  isMobile={isMobile}
                  href={link.href}
                  highlightItem={index === links.length - 1 && highlightLastItem}
                >
                  {link.label}
                </BreadcrumbsItem>
                {index < links.length - 1 && <IconChevronRight color={theme.white} />}
              </React.Fragment>
            ))}
          </Box>
          {children}
        </>
      )}
    </BreadcrumbsWrapper>
  );
}
